import {DayPeriod, StrengthTarget} from '../types'

import {getTodayISOInTimezone, compareDateStrings} from './dateUtils'

export function upsertTarget(
  targets: StrengthTarget[],
  target: StrengthTarget,
  isUpdate: boolean
): StrengthTarget[] {
  if (isUpdate) {
    return targets.map((t) => (t.id === target.id ? target : t))
  }
  return [...targets, target]
}

export function recalculateValidToDates(
  targets: StrengthTarget[],
  targetDayPeriod: number,
  changedTargetId: string
): StrengthTarget[] {
  const samePeriodTargets = targets
    .filter((t) => t.dayPeriod === targetDayPeriod)
    .sort((a, b) => {
      if (!a.validFrom) return 1
      if (!b.validFrom) return -1
      return a.validFrom.localeCompare(b.validFrom)
    })

  const currentIndex = samePeriodTargets.findIndex((t) => t.id === changedTargetId)

  if (currentIndex === -1) {
    return targets
  }

  const nextTarget = samePeriodTargets[currentIndex + 1]
  if (nextTarget) {
    samePeriodTargets[currentIndex] = {
      ...samePeriodTargets[currentIndex],
      validTo: nextTarget.validFrom
    }
  } else {
    samePeriodTargets[currentIndex] = {
      ...samePeriodTargets[currentIndex],
      validTo: undefined
    }
  }

  const previousTarget = samePeriodTargets[currentIndex - 1]
  if (previousTarget) {
    samePeriodTargets[currentIndex - 1] = {
      ...samePeriodTargets[currentIndex - 1],
      validTo: samePeriodTargets[currentIndex].validFrom
    }
  }

  return targets.map((t) => {
    if (t.dayPeriod === targetDayPeriod) {
      const updated = samePeriodTargets.find((st) => st.id === t.id)
      return updated || t
    }
    return t
  })
}

export function deleteTarget(
  targets: StrengthTarget[],
  targetToDelete: StrengthTarget
): StrengthTarget[] {
  const remainingTargets = targets.filter((t) => t.id !== targetToDelete.id)

  const samePeriodTargets = remainingTargets
    .filter((t) => t.dayPeriod === targetToDelete.dayPeriod)
    .sort((a, b) => {
      if (!a.validFrom) return 1
      if (!b.validFrom) return -1
      return a.validFrom.localeCompare(b.validFrom)
    })

  const recalculatedTargets = samePeriodTargets.map((target, index) => {
    const nextTarget = samePeriodTargets[index + 1]

    if (nextTarget) {
      return {
        ...target,
        validTo: nextTarget.validFrom
      }
    } else {
      return {
        ...target,
        validTo: undefined
      }
    }
  })

  return remainingTargets.map((t) => {
    if (t.dayPeriod === targetToDelete.dayPeriod) {
      const updated = recalculatedTargets.find((rt) => rt.id === t.id)
      return updated || t
    }
    return t
  })
}

export function getCurrentTargetForPeriod(
  targets: StrengthTarget[],
  dayPeriod: DayPeriod,
  todayISO: string
): StrengthTarget | undefined {
  const validTargets = targets.filter((target) => {
    if (target.dayPeriod !== dayPeriod) return false
    if (compareDateStrings(target.validFrom, todayISO) > 0) return false
    if (target.validTo && compareDateStrings(target.validTo, todayISO) < 0) return false
    return true
  })

  if (validTargets.length === 0) return undefined
  if (validTargets.length === 1) return validTargets[0]

  return validTargets.reduce((latest, current) => {
    return compareDateStrings(current.validFrom, latest.validFrom) > 0 ? current : latest
  })
}

export function markCurrentTargets(targets: StrengthTarget[], timezone: string): StrengthTarget[] {
  const today = getTodayISOInTimezone(timezone)

  const targetsByPeriod: Record<number, StrengthTarget[]> = {}
  targets.forEach((target) => {
    if (!targetsByPeriod[target.dayPeriod]) {
      targetsByPeriod[target.dayPeriod] = []
    }
    targetsByPeriod[target.dayPeriod].push(target)
  })

  const currentTargetIds = new Set<string>()
  Object.values(targetsByPeriod).forEach((periodTargets) => {
    const validTargets = periodTargets.filter((target) => {
      const isValidFrom = compareDateStrings(target.validFrom, today) <= 0
      const isValidTo = !target.validTo || compareDateStrings(target.validTo, today) > 0
      return isValidFrom && isValidTo
    })

    if (validTargets.length > 0) {
      const currentTarget = validTargets.reduce((latest, current) => {
        return compareDateStrings(current.validFrom, latest.validFrom) > 0 ? current : latest
      })
      currentTargetIds.add(currentTarget.id)
    }
  })

  return targets.map((target) => ({
    ...target,
    isCurrent: currentTargetIds.has(target.id)
  }))
}

export function sortTargetsByValidity(
  targets: StrengthTarget[],
  timezone: string
): StrengthTarget[] {
  const today = getTodayISOInTimezone(timezone)

  const current = targets.filter((t) => t.isCurrent)
  const future = targets.filter((t) => !t.isCurrent && compareDateStrings(t.validFrom, today) > 0)
  const historical = targets.filter(
    (t) => !t.isCurrent && compareDateStrings(t.validFrom, today) <= 0
  )

  future.sort((a, b) => {
    if (!a.validFrom) return 1
    if (!b.validFrom) return -1
    return a.validFrom.localeCompare(b.validFrom)
  })

  historical.sort((a, b) => {
    if (!a.validFrom) return 1
    if (!b.validFrom) return -1
    return a.validFrom.localeCompare(b.validFrom)
  })

  return [...current, ...future, ...historical]
}
